import { AnalyticsPanel } from '@/shared/components/ui/AnalyticsPanel';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { ROUTES } from '@/routes/paths';
import { Search, Filter, Calendar, RefreshCw, ArrowDownLeft, ArrowUpRight, Scale, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';

/**
 * Where a ledger row's source document can actually be opened today.
 * Only sourceCollections with a real detail view get a link — the rest
 * (contra, depreciation, currency adjustments, opening balances, and
 * payments, which has no detail view of its own yet) stay plain rows rather
 * than a link that leads nowhere.
 */
function linkFor(line: any): string | null {
  const source = line.source;
  if (!source || !source.referenceId) return null;
  switch (source.sourceCollection) {
    case 'JournalVoucher':
      return `${ROUTES.accountsTransactions}?journalId=${source.referenceId}`;
    case 'AccountsSalesInvoice':
      return `${ROUTES.accountsSales}?invoiceId=${source.referenceId}`;
    case 'AccountsPurchaseBill':
      return `${ROUTES.accountsPurchases}?billId=${source.referenceId}`;
    default:
      return null;
  }
}

export function AccountTransactionsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, totalNet: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch accounts list for dropdown filter
  useEffect(() => {
    api.get('/api/accounting/accounts')
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
          setAccounts(list);
        }
      })
      .catch(() => {});
  }, []);

  const fetchTransactions = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedAccountId) params.set('accountId', selectedAccountId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    api.get(`/api/accounting/transactions?${params.toString()}`)
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
          setItems(list);
          setTotals(data.data?.totals || { totalDebit: 0, totalCredit: 0, totalNet: 0 });
        }
      })
      .catch((err) => {
        console.error('Failed to fetch transactions:', err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTransactions();
  }, [selectedAccountId, startDate, endDate]);

  return (
    <div className="">
      <AccountingSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Ledger Explorer
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Search and inspect all posted general ledger journal lines across the enterprise.
            </p>
          </div>

          <button
            onClick={fetchTransactions}
            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-lg font-medium text-xs transition flex items-center gap-1.5 border border-slate-300 dark:border-slate-700 self-start md:self-auto"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh Ledger
          </button>
        </div>

        {/* Totals Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Debit */}
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Total Debit
                </span>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-black tracking-tight text-slate-900 font-mono">
                    {formatINR(totals.totalDebit / 100)}
                  </span>
                </div>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600 shadow-xs transition-transform group-hover:scale-105">
                <ArrowDownLeft className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
              <span>Debit Side Inflow</span>
              <span className="font-bold text-blue-700">Dr</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-blue-600 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>

          {/* Total Credit */}
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Total Credit
                </span>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-black tracking-tight text-slate-900 font-mono">
                    {formatINR(totals.totalCredit / 100)}
                  </span>
                </div>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-600 shadow-xs transition-transform group-hover:scale-105">
                <ArrowUpRight className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
              <span>Credit Side Outflow</span>
              <span className="font-bold text-emerald-700">Cr</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>

          {/* Net Movement */}
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Net Movement
                </span>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-black tracking-tight text-purple-600 font-mono">
                    {formatINR(totals.totalNet / 100)}
                  </span>
                </div>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-purple-100 bg-purple-50 text-purple-600 shadow-xs transition-transform group-hover:scale-105">
                <Scale className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
              <span>Debit vs Credit Balance</span>
              <span className="font-bold text-purple-700">Net</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-purple-600 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {/* Ledger Movement & Inflow/Outflow Timeline Chart */}
        <AnalyticsPanel storageKey="ledger" title="Ledger posting trend" description="Debits and credits over time">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-bold text-slate-900">Ledger Posting Trajectory</h3>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Chronological movement of debits and credits across the general ledger
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs font-semibold">
              <div className="flex items-center gap-1.5 text-blue-700">
                <span className="w-3 h-3 rounded-full bg-blue-600 inline-block" />
                Debit Flow (₹)
              </div>
              <div className="flex items-center gap-1.5 text-emerald-700">
                <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" />
                Credit Flow (₹)
              </div>
            </div>
          </div>

          <div className="h-60 w-full">
            {(() => {
              if (!items || items.length === 0) {
                return (
                  <div className="h-full flex items-center justify-center text-xs text-slate-400">
                    No transactions matching current filters to plot trajectory.
                  </div>
                );
              }

              // Group by date or transaction index (up to 15 intervals)
              const dateMap: Record<string, { label: string; debit: number; credit: number }> = {};
              items.slice(0, 50).forEach((tx) => {
                const d = tx.date ? new Date(tx.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : 'Recent';
                if (!dateMap[d]) {
                  dateMap[d] = { label: d, debit: 0, credit: 0 };
                }
                dateMap[d].debit += (tx.debit || 0) / 100;
                dateMap[d].credit += (tx.credit || 0) / 100;
              });

              const chartData = Object.values(dateMap).slice(-12);

              return (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorDebit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563EB" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#2563EB" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="colorCredit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={{ stroke: '#CBD5E1' }}
                      tick={{ fill: '#64748B', fontSize: 11, fontWeight: 500 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={{ stroke: '#CBD5E1' }}
                      tick={{ fill: '#64748B', fontSize: 11 }}
                      tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                    />
                    <Tooltip
                      formatter={(val: any) => [formatINR(Number(val)), 'Amount']}
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="debit"
                      name="Debit"
                      stroke="#2563EB"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorDebit)"
                    />
                    <Area
                      type="monotone"
                      dataKey="credit"
                      name="Credit"
                      stroke="#10B981"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorCredit)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        </div>
        </AnalyticsPanel>

        {/* Filter Toolbar */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search narration or reference..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTransactions()}
              className="pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 max-w-xs"
            >
              <option value="">All GL Accounts</option>
              {accounts.map((acc) => (
                <option key={acc._id} value={acc._id}>
                  {acc.code} — {acc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-xs"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-xs"
            />
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">Loading ledger transactions...</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-400 dark:text-slate-500">
              No journal transactions found for the selected filters.
            </div>
          ) : (
            <table className="w-full text-left text-sm text-slate-700 dark:text-slate-300">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 font-semibold text-slate-900 dark:text-slate-100">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Description / Narration</th>
                  <th className="px-4 py-3 text-right">Debit (₹)</th>
                  <th className="px-4 py-3 text-right">Credit (₹)</th>
                  <th className="px-4 py-3 text-right">Running Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((line) => {
                  const href = linkFor(line);
                  return (
                  <tr
                    key={line._id}
                    onClick={href ? () => navigate(href) : undefined}
                    className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/40 ${href ? 'cursor-pointer' : ''}`}
                    title={href ? 'Open the source document' : undefined}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {line.lineDate ? new Date(line.lineDate).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {line.description || 'General Journal Posting'}
                      {line.reference && (
                        <span className="ml-2 text-xs font-mono text-slate-400 dark:text-slate-500">
                          [{line.reference}]
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-slate-900 dark:text-slate-100">
                      {line.debit > 0 ? formatINR(line.debit / 100) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-slate-900 dark:text-slate-100">
                      {line.credit > 0 ? formatINR(line.credit / 100) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-indigo-600 dark:text-indigo-400">
                      {line.runningBalance !== null && line.runningBalance !== undefined
                        ? formatINR(line.runningBalance / 100)
                        : '—'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountTransactionsPage;
